import { createSignal, For, Show, onMount } from "solid-js";
import { api, type Organization, type Project, type RealtimeConnectionInfo } from "../shared";
import { createDefaultFilter, type DebugFilterState } from "../hooks/useDebugWebSocket";
import { KNOWN_EVENT_TYPES, EVENT_CATEGORIES } from "@searchgrid/shared/realtime/filter";

function CollapsibleSection(props: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: any;
}) {
  const [open, setOpen] = createSignal(props.defaultOpen ?? true);
  return (
    <div class="border-b border-gray-800/50 py-2">
      <button
        onClick={() => setOpen((o) => !o)}
        class="flex items-center gap-1.5 w-full text-left text-xs text-gray-400 hover:text-gray-200 cursor-pointer font-medium"
      >
        <span class={`transition-transform ${open() ? "rotate-90" : ""}`}>&#9656;</span>
        <span>{props.title}</span>
        <Show when={props.count > 0}>
          <span class="bg-cyan-900 text-cyan-300 text-[10px] px-1.5 py-0.5 rounded-full">{props.count}</span>
        </Show>
      </button>
      <Show when={open()}>
        <div class="mt-2 pl-4">{props.children}</div>
      </Show>
    </div>
  );
}

export function RealtimeFilterBar(props: {
  connected: boolean;
  onConnect: (filters: DebugFilterState, persist: boolean) => void;
  onDisconnect: () => void;
  onFilterUpdate: (filters: Partial<DebugFilterState>) => void;
  connections: RealtimeConnectionInfo[];
  selectedConnectionIds: string[];
  onClearConnections: () => void;
}) {
  const [scopeMode, setScopeMode] = createSignal<"all" | "project">("all");
  const [orgs, setOrgs] = createSignal<Organization[]>([]);
  const [projects, setProjects] = createSignal<Project[]>([]);
  const [selectedOrgId, setSelectedOrgId] = createSignal("");
  const [selectedProjectId, setSelectedProjectId] = createSignal("");
  const [selectedRoles, setSelectedRoles] = createSignal<Set<string>>(new Set());
  const [selectedEventTypes, setSelectedEventTypes] = createSignal<Set<string>>(new Set());
  const [customEventTypes, setCustomEventTypes] = createSignal("");
  const [userIdInput, setUserIdInput] = createSignal("");
  const [persist, setPersist] = createSignal(false);

  onMount(() => {
    api.organizations.list().then(setOrgs).catch(() => {});
  });

  function onOrgChange(id: string) {
    setSelectedOrgId(id);
    setSelectedProjectId("");
    if (id) {
      api.organizations.projects(id).then(setProjects).catch(() => setProjects([]));
    } else {
      setProjects([]);
    }
  }

  function buildFilters(): DebugFilterState {
    const filter = createDefaultFilter();
    if (scopeMode() === "project" && selectedProjectId()) {
      filter.projectIds = [selectedProjectId()];
    }
    filter.roles = Array.from(selectedRoles());
    const eventTypes = Array.from(selectedEventTypes());
    const custom = customEventTypes().split(",").map((s) => s.trim()).filter(Boolean);
    filter.eventTypes = [...eventTypes, ...custom];
    filter.userIds = userIdInput().split(",").map((s) => s.trim()).filter(Boolean);
    return filter;
  }

  function handleConnect() {
    props.onConnect(buildFilters(), persist());
  }

  function applyFilters() {
    props.onFilterUpdate(buildFilters());
  }

  function toggleRole(role: string) {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  }

  function toggleEventType(type: string) {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function selectAllEventTypes() {
    const all = new Set(KNOWN_EVENT_TYPES);
    setSelectedEventTypes(all);
  }

  function clearEventTypes() {
    setSelectedEventTypes(new Set());
    setCustomEventTypes("");
  }

  function toggleCategory(types: readonly string[]) {
    setSelectedEventTypes((prev) => {
      const next = new Set(prev);
      const allSelected = types.every((t) => next.has(t));
      if (allSelected) {
        for (const t of types) next.delete(t);
      } else {
        for (const t of types) next.add(t);
      }
      return next;
    });
  }

  const activeFilterCount = () =>
    selectedRoles().size + selectedEventTypes().size + (customEventTypes() ? 1 : 0) + (userIdInput() ? 1 : 0) +
    (scopeMode() === "project" && selectedProjectId() ? 1 : 0);

  return (
    <div class="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-800/50">
        <div class="flex items-center gap-3">
          <h3 class="text-sm font-semibold text-gray-300">Filters</h3>
          <Show when={activeFilterCount() > 0}>
            <span class="bg-cyan-900 text-cyan-300 text-[10px] px-2 py-0.5 rounded-full">{activeFilterCount()}</span>
          </Show>
        </div>
        <div class="flex items-center gap-2">
          <label class="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input type="checkbox" checked={persist()} onChange={(e) => setPersist(e.currentTarget.checked)} class="rounded bg-gray-700 border-gray-600 cursor-pointer" />
            Persist to DB
          </label>
          <button
            onClick={handleConnect}
            disabled={props.connected}
            class={`px-4 py-1.5 rounded text-sm font-medium cursor-pointer ${
              props.connected
                ? "bg-emerald-900/30 border border-emerald-700 text-emerald-300"
                : "bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-50"
            }`}
          >
            {props.connected ? "Connected" : "Connect"}
          </button>
          <button onClick={props.onDisconnect} class="px-3 py-1.5 bg-gray-800 text-gray-400 rounded text-sm hover:text-gray-200 cursor-pointer">Disconnect</button>
          <button onClick={applyFilters} disabled={!props.connected} class="px-3 py-1.5 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600 disabled:opacity-50 cursor-pointer">Apply</button>
        </div>
      </div>

      <div class="px-4 py-1">
        <CollapsibleSection title="Scope" count={scopeMode() === "project" && selectedProjectId() ? 1 : 0}>
          <div class="flex items-center gap-3 flex-wrap">
            <select value={scopeMode()} onChange={(e) => setScopeMode(e.currentTarget.value as "all" | "project")} class="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 cursor-pointer">
              <option value="all">All Traffic</option>
              <option value="project">By Project</option>
            </select>
            <Show when={scopeMode() === "project"}>
              <select value={selectedOrgId()} onChange={(e) => onOrgChange(e.currentTarget.value)} class="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 cursor-pointer">
                <option value="">All organizations</option>
                <For each={orgs()}>{(o) => <option value={o.id}>{o.name}</option>}</For>
              </select>
              <select value={selectedProjectId()} onChange={(e) => setSelectedProjectId(e.currentTarget.value)} class="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 cursor-pointer" disabled={!selectedOrgId()}>
                <option value="">Select project</option>
                <For each={projects()}>{(p) => <option value={p.id}>{p.name}</option>}</For>
              </select>
            </Show>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Roles" count={selectedRoles().size}>
          <div class="flex items-center gap-4 flex-wrap">
            {["searcher", "manager", "debug"].map((role) => (
              <label class="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer hover:text-gray-200">
                <input type="checkbox" checked={selectedRoles().has(role)} onChange={() => toggleRole(role)} class="rounded bg-gray-700 border-gray-600 cursor-pointer" />
                <span class={`${role === "searcher" ? "text-cyan-400" : role === "manager" ? "text-purple-400" : "text-amber-400"}`}>{role}</span>
              </label>
            ))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Event Types" count={selectedEventTypes().size + (customEventTypes() ? 1 : 0)} defaultOpen={false}>
          <div class="space-y-2">
            <div class="flex items-center gap-2">
              <button onClick={selectAllEventTypes} class="text-[10px] px-2 py-0.5 bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer">All</button>
              <button onClick={clearEventTypes} class="text-[10px] px-2 py-0.5 bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer">None</button>
            </div>
            <For each={Object.entries(EVENT_CATEGORIES)}>
              {([cat, types]) => (
                <div>
                  <button onClick={() => toggleCategory(types)} class={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer ${
                    types.every((t) => selectedEventTypes().has(t)) ? "bg-cyan-900 text-cyan-300" : "bg-gray-800 text-gray-500 hover:text-gray-300"
                  }`}>
                    {cat} ({types.length})
                  </button>
                  <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 ml-2">
                    <For each={[...types]}>
                      {(type) => (
                        <label class="flex items-center gap-1 text-[10px] text-gray-400 cursor-pointer hover:text-gray-200">
                          <input type="checkbox" checked={selectedEventTypes().has(type)} onChange={() => toggleEventType(type)} class="rounded bg-gray-700 border-gray-600 cursor-pointer" />
                          <span class="truncate max-w-[110px]">{type}</span>
                        </label>
                      )}
                    </For>
                  </div>
                </div>
              )}
            </For>
            <div class="flex items-center gap-2">
              <span class="text-[10px] text-gray-600">Custom:</span>
              <input type="text" value={customEventTypes()} onInput={(e) => setCustomEventTypes(e.currentTarget.value)} placeholder="type-a,type-b" class="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-[10px] text-gray-200 w-40" />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Users" count={userIdInput() ? 1 : 0} defaultOpen={false}>
          <div class="flex items-center gap-2">
            <input type="text" value={userIdInput()} onInput={(e) => setUserIdInput(e.currentTarget.value)} placeholder="user-id-1, user-id-2" class="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 w-64" />
          </div>
        </CollapsibleSection>
      </div>

      <Show when={props.selectedConnectionIds.length > 0}>
        <div class="px-4 py-2 border-t border-gray-800/50 bg-cyan-900/10">
          <div class="flex items-center gap-2 flex-wrap text-xs">
            <span class="text-cyan-400 font-medium">Connections:</span>
            <For each={props.selectedConnectionIds}>
              {(id) => <span class="bg-cyan-900/50 text-cyan-300 rounded px-1.5 py-0.5 font-mono">{id.slice(0, 12)}</span>}
            </For>
            <button onClick={props.onClearConnections} class="text-gray-500 hover:text-gray-300 cursor-pointer ml-1">clear</button>
          </div>
        </div>
      </Show>
    </div>
  );
}
